// The place-the-notes editors' music (voicingPins.ts slice 3): what a placed
// note IS against the chord, and where a written melody note sits on a string.
// Pure so the fretboard and keyboard editors share one reading and the tests
// need no DOM.

import { CHORD_TYPES, chordToneName, type ChordTypeId } from './chords';
import { pitchClassOf, type PitchClass } from './notes';
import { TENSION_NAMES, admittedTensions } from './tensions';
import { STRING_LAYOUTS, frettedMidi, type StringLayoutId } from './tuning';
import type { VoicingFamily } from './voicingFamilies';

export type PlacedNoteRole = 'tone' | 'tension' | 'outside';

export interface PlacedNote {
  /** The degree label a lead sheet would print: R, 3, ♭7, 9, ♯11… */
  label: string;
  /** A chord tone, an admitted tension, or a note the chord does not own. */
  role: PlacedNoteRole;
}

const mod12 = (n: number) => ((n % 12) + 12) % 12;

/** How a placed pitch class reads against the chord. Every note is admitted —
 * the editor names what you placed, it never refuses — so an outside note is
 * marked, and the pin is still yours to keep. */
export function placedNoteRole(
  pc: PitchClass,
  rootDisplay: string,
  chordType: ChordTypeId,
): PlacedNote {
  const rootPc = pitchClassOf(rootDisplay);
  const interval = mod12(pc - rootPc);
  const tones = new Set([0, ...CHORD_TYPES[chordType].intervals.map(mod12)]);
  if (tones.has(interval)) {
    return { label: chordToneName(pc, rootPc, chordType), role: 'tone' };
  }
  if (admittedTensions(chordType).includes(interval)) {
    return { label: TENSION_NAMES[interval] ?? String(interval), role: 'tension' };
  }
  return { label: chordToneName(pc, rootPc, chordType), role: 'outside' };
}

/** The fret that puts `midi` on `string`, or null when the string cannot
 * reach it inside `maxFret` frets. */
export function fretForMidi(string: number, midi: number, maxFret = 20): number | null {
  const fret = midi - frettedMidi(string, 0);
  return fret >= 0 && fret <= maxFret ? fret : null;
}

/** The layouts a grip may be placed on, with the string names a player reads. */
export const PLACEABLE_LAYOUTS: { id: StringLayoutId; label: string }[] = (
  Object.keys(STRING_LAYOUTS) as StringLayoutId[]
).map((id) => ({
  id,
  label:
    id === 'joe-pass'
      ? 'All strings'
      : STRING_LAYOUTS[id].strings.map((s) => ['E', 'A', 'D', 'G', 'B', 'e'][s]).join(' '),
}));

/** The layout a fingering was placed on, or the drop family's layout for its
 * string set — the editor opens on the grip it was asked about. */
export function layoutOfFingering(fingering: {
  stringLayout?: StringLayoutId;
  stringSet: 'middle' | 'upper';
  voicingFamily?: VoicingFamily;
}): StringLayoutId {
  if (fingering.stringLayout) return fingering.stringLayout;
  const family = fingering.voicingFamily ?? 'drop-2';
  return (
    (Object.keys(STRING_LAYOUTS) as StringLayoutId[]).find(
      (id) =>
        STRING_LAYOUTS[id].family === family && STRING_LAYOUTS[id].register === fingering.stringSet,
    ) ?? 'adgb'
  );
}

/** The frets a placed grip opens on: the current grip's frets on the layout's
 * strings, nulls elsewhere. */
export function fretsOnLayout(
  frets: readonly (number | null)[],
  layout: StringLayoutId,
): (number | null)[] {
  const strings = new Set<number>(STRING_LAYOUTS[layout].strings);
  return frets.map((f, i) => (strings.has(i) ? (f ?? null) : null));
}

/** The lowest fret the editor's window should open on for these frets:
 * open-position for an empty or open grip, else a fret below the lowest. */
export function windowStartFor(frets: readonly (number | null)[], windowFrets = 5): number {
  const fretted = frets.filter((f): f is number => f != null && f > 0);
  if (!fretted.length) return 1;
  const low = Math.min(...fretted);
  const high = Math.max(...fretted);
  if (high - low + 1 > windowFrets) return low;
  return Math.max(1, Math.min(low, high - windowFrets + 1));
}
